// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/SwipeSupervivencia.jsx
//  FORMATO 2 — SWIPE DE SUPERVIVENCIA
//
//  Derecha si suena bien. Izquierda si está mal. Y cada vez más rápido.
//
//  ── LA PEDAGOGÍA ────────────────────────────────────────────────────────────
//  Aquí no se estudia ni una regla. Se entrena reconocimiento de patrones: el
//  alumno aprende qué "suena bien" por exposición masiva, igual que un nativo.
//  Todas las trampas son calcos reales del español — el error propio, no un
//  error genérico de libro.
//
//  El reloj es parte del método, no adorno: con tiempo de sobra el alumno
//  analiza la gramática y eso es justo lo que NO queremos entrenar. Con 4
//  segundos, decide de oído.
//
//  ── LA DOPAMINA ─────────────────────────────────────────────────────────────
//  Recompensa variable: la racha multiplica los puntos y acelera el mazo. Un
//  fallo la rompe entera. Ese riesgo creciente es lo que hace que la siguiente
//  tarjeta importe.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';

import { SWIPE, byContext, shuffle } from './dojoBank';
import { fxHit, fxCombo, fxMiss, fxTimeout, fxStart } from '../audio/fx';
import { useFeedback } from './useFeedback';
import FeedbackLayer from './FeedbackLayer';
import DojoResult from './DojoResult';

const ROUNDS = 14;
const START_MS = 6000;    // primera tarjeta: hay tiempo de leer
const FLOOR_MS = 2600;    // suelo: por debajo deja de ser jugable
const STEP_MS = 260;      // cuánto se acelera por tarjeta
const DRAG_TRIGGER = 84;  // px para que cuente como swipe

export default function SwipeSupervivencia({ ctx, onExit }) {
  const [deck] = useState(() => shuffle(byContext(SWIPE, ctx)).slice(0, ROUNDS));
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState('intro');   // intro|play|note|done
  const [points, setPoints] = useState(0);
  const [hits, setHits] = useState(0);
  const [streak, setStreak] = useState(0);
  const [best, setBest] = useState(0);
  const [last, setLast] = useState(null);        // {ok, item, timeout}
  const [bar, setBar] = useState(1);
  const [drag, setDrag] = useState(0);
  const [flying, setFlying] = useState(0);

  const { flash, floats, fire } = useFeedback();

  const rafRef = useRef(0);
  const startX = useRef(null);
  const answered = useRef(false);

  const item = deck[i];
  const limitMs = Math.max(FLOOR_MS, START_MS - i * STEP_MS);

  const stopBar = () => cancelAnimationFrame(rafRef.current);
  useEffect(() => stopBar, []);

  /* ── Resolución de una tarjeta ─────────────────────────────────────────── */
  const resolve = useCallback((answer) => {
    if (answered.current) return;
    answered.current = true;
    stopBar();

    const timeout = answer === null;
    const ok = !timeout && answer === item.ok;

    setFlying(timeout ? 0 : (answer ? 1 : -1));

    if (ok) {
      const n = streak + 1;
      const mult = n >= 5 ? 2 : 1;      // "Fuego x2" a partir de 5 seguidas
      setStreak(n);
      setBest((b) => Math.max(b, n));
      setHits((h) => h + 1);
      setPoints((p) => p + 10 * mult);
      if (n >= 5) fxCombo(n); else fxHit();
      fire(true, `+${10 * mult}`);
    } else {
      setStreak(0);
      if (timeout) fxTimeout(); else fxMiss();
      fire(false, timeout ? '⏱' : '✕');
    }

    setLast({ ok, timeout, item });
    setPhase('note');
  }, [item, streak, fire]);

  /* ── Reloj de la tarjeta ───────────────────────────────────────────────── */
  const runClock = useCallback(() => {
    const t0 = performance.now();
    const step = (now) => {
      const left = Math.max(0, 1 - (now - t0) / limitMs);
      setBar(left);
      if (left <= 0) { resolve(null); return; }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [limitMs, resolve]);

  useEffect(() => {
    if (phase !== 'play') return undefined;
    answered.current = false;
    setDrag(0);
    setFlying(0);
    setBar(1);
    runClock();
    return stopBar;
  }, [phase, i, runClock]);

  /* ── Gestos ────────────────────────────────────────────────────────────── */
  const onDown = (e) => {
    if (phase !== 'play') return;
    startX.current = (e.touches ? e.touches[0] : e).clientX;
  };
  const onMove = (e) => {
    if (startX.current === null) return;
    const x = (e.touches ? e.touches[0] : e).clientX;
    setDrag(x - startX.current);
  };
  const onUp = () => {
    if (startX.current === null) return;
    const d = drag;
    startX.current = null;
    if (Math.abs(d) >= DRAG_TRIGGER) resolve(d > 0);
    else setDrag(0);
  };

  /* ── Teclado: el formato también se juega en escritorio ────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      if (phase === 'play') {
        if (e.key === 'ArrowRight') resolve(true);
        if (e.key === 'ArrowLeft') resolve(false);
      } else if (phase === 'note' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        advance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const advance = () => {
    if (i + 1 >= deck.length) { setPhase('done'); return; }
    setI((n) => n + 1);
    setPhase('play');
  };

  const begin = () => { fxStart(); setPhase('play'); };

  /* ── Render ────────────────────────────────────────────────────────────── */
  if (phase === 'done') {
    return (
      <DojoResult
        format="swipe"
        score={points}
        total={deck.length}
        headline={points}
        label="Puntos"

        recap={[
          { v: `${hits}/${deck.length}`, k: 'aciertos' },
          { v: best, k: 'mejor racha' },
          { v: `${(limitMs / 1000).toFixed(1)}s`, k: 'última tarjeta' },
        ]}
        note={best >= 5
          ? 'Encadenaste 5 o más: ahí ya no estabas analizando gramática, estabas reconociendo el patrón.'
          : 'La racha es lo que importa. Cinco seguidas activan el multiplicador.'}
        onAgain={() => window.location.reload()}
        onExit={onExit}
      />
    );
  }

  if (phase === 'intro') {
    return (
      <div className="dj-stage dj-screen">
        <p className="dj-hero-tag">🃏 Formato 02 · Patrón</p>
        <h2 className="dj-h1"><span className="dj-glow">SWIPE</span></h2>
        <p className="dj-lead">
          Derecha si la frase <b>suena bien</b>. Izquierda si <b>está mal</b>.
          Todas las trampas son calcos del español — los errores que de verdad
          sueltas. El mazo acelera con cada tarjeta.
        </p>
        <button type="button" className="dj-btn" style={{ width: '100%' }} onClick={begin}>
          Empezar ({deck.length} tarjetas) →
        </button>
      </div>
    );
  }

  const rot = drag / 18;
  const flyX = flying * 520;

  return (
    <>
      <FeedbackLayer flash={flash} floats={floats} />

      <div className="dj-hud">
        <button type="button" className="dj-quit" onClick={onExit}>← Salir</button>
        <span className="dj-badge">Swipe</span>
        <span className="dj-spacer" />
        {streak >= 5 && <span className="dj-streak dj-pulse" key={streak}>🔥 FUEGO ×2</span>}
        <span className="dj-score-badge">{points}</span>
      </div>

      <div className="dj-timer">
        <div
          className={`dj-timer-fill ${bar < 0.3 ? 'dj-crit' : bar < 0.6 ? 'dj-warn' : ''}`}
          style={{ transform: `scaleX(${phase === 'play' ? bar : 0})` }}
        />
      </div>
      <div className="dj-meta" style={{ marginTop: '-0.85rem', marginBottom: '1rem' }}>
        <span className="dj-progress">Tarjeta {i + 1} de {deck.length}</span>
        <span className="dj-progress">{(limitMs / 1000).toFixed(1)}s por tarjeta</span>
      </div>

      <div className="dj-swipe-hint">
        <span>← Está mal</span>
        <span>Suena bien →</span>
      </div>

      <div className="dj-deck">
        {deck[i + 1] && <div className="dj-swipe dj-swipe-back" aria-hidden="true" />}
        <div
          className={`dj-swipe ${startX.current !== null ? 'dj-drag' : ''} ${flying ? 'dj-fly' : ''}`}
          style={{ transform: `translateX(${flying ? flyX : drag}px) rotate(${flying ? flying * 16 : rot}deg)` }}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
        >
          <span className="dj-swipe-tag">{item.ctx.replace('_', ' ')}</span>
          <span className="dj-stamp dj-stamp-ok" style={{ opacity: Math.max(0, drag / DRAG_TRIGGER) }}>BIEN</span>
          <span className="dj-stamp dj-stamp-no" style={{ opacity: Math.max(0, -drag / DRAG_TRIGGER) }}>MAL</span>
          <p className="dj-swipe-text">{item.text}</p>
        </div>
      </div>

      {phase === 'note' && last && (
        <div className="dj-note">
          <b style={{ color: last.ok ? 'var(--dj-go)' : 'var(--dj-no)' }}>
            {last.ok ? '✓ Correcto' : last.timeout ? '⏱ Se acabó el tiempo' : '✕ Fallaste'}
          </b>
          {' — '}
          {last.item.ok
            ? <>la frase estaba bien. </>
            : <><s>{last.item.text}</s> → <em>{last.item.fix}</em>. </>}
          {last.item.note}
        </div>
      )}

      {phase === 'note' ? (
        <div className="dj-keys" style={{ gridTemplateColumns: '1fr' }}>
          <button type="button" className="dj-btn" onClick={advance}>
            {i + 1 >= deck.length ? 'Ver resultado →' : 'Siguiente →'}
          </button>
        </div>
      ) : (
        <div className="dj-keys">
          <button type="button" className="dj-key dj-key-no" onClick={() => resolve(false)}>✕ Está mal</button>
          <button type="button" className="dj-key dj-key-ok" onClick={() => resolve(true)}>Suena bien ✓</button>
        </div>
      )}
    </>
  );
}
